import { Capacitor } from '@capacitor/core';

import { UnknownRuntimeError } from './errors';
import { RUNTIME_CAPABILITIES } from './capabilities';
import { isRuntimeKind, type PosRuntime, type RuntimeKind } from './types';

/**
 * Runtime detection — the composition root's ONLY Capacitor dependency.
 *
 * PLAN §4.1: this is the single module in code written or modified from Phase 1
 * onward that may call `Capacitor.getPlatform()` / `Capacitor.isNativePlatform()`.
 * Everything else asks `getRuntime()` or `hasCapability()`.
 *
 * AMENDMENT A3.1: Model B (`window.CapacitorCustomPlatform = { name: 'electron' }`)
 * is a *compatibility* mechanism so existing Capacitor-shaped code keeps working.
 * It is not the selection authority — this composition root is. Where the two
 * could disagree, the table in `capabilities.ts` wins.
 *
 * Deliberately NOT memoized: suites that `vi.spyOn(Capacitor, 'getPlatform')`
 * (e.g. `src/features/diagnostics/__tests__/native-platform.ts`) must keep
 * working, and the answer is a couple of property reads.
 */

let runtimeKindOverride: RuntimeKind | null = null;

/**
 * Replace the detector for the duration of a test.
 * Production code must never call this; use `src/platform/testing`.
 * @internal
 */
export function __setRuntimeKindOverride(kind: RuntimeKind | null): void {
  runtimeKindOverride = kind;
}

/** @internal */
export function __getRuntimeKindOverride(): RuntimeKind | null {
  return runtimeKindOverride;
}

/**
 * The detected runtime kind.
 *
 * @throws UnknownRuntimeError when Capacitor reports a platform with no
 * capability row. Failing here is deliberate: the alternative is the current
 * behaviour, where an unknown platform surfaces as `Unsupported platform`
 * thrown deep inside the database layer during boot.
 */
export function detectRuntimeKind(): RuntimeKind {
  if (runtimeKindOverride !== null) return runtimeKindOverride;
  const detected = Capacitor.getPlatform();
  if (!isRuntimeKind(detected)) throw new UnknownRuntimeError(detected);
  return detected;
}

/**
 * The resolved runtime: kind, the three A3.2 predicates, and its capability row.
 *
 * `isNative` mirrors `Capacitor.isNativePlatform()` (`getPlatform() !== 'web'`),
 * so the Electron shell is native — a desktop register genuinely is a packaged
 * install with real hardware. `isMobile` is the *separate* question, and it is
 * the one permission prompts, Capgo OTA and mobile chrome actually mean.
 */
export function getRuntime(): PosRuntime {
  const kind = detectRuntimeKind();
  return {
    kind,
    isNative: kind !== 'web',
    isMobile: kind === 'ios' || kind === 'android',
    isDesktopShell: kind === 'electron',
    capabilities: RUNTIME_CAPABILITIES[kind],
  };
}
